import { useAppSelector } from '@/hooks'
import axios from 'axios'
import { useCallback, useEffect, useRef, useState } from 'react'
import { GlobalSearchResponse, SearchCategory, SearchResultItem, SearchScope } from '../types/types'
import { useBoxTruckProfilesMode } from '@/features/box-truck-loads/hooks/useBoxTruckProfilesMode'

const DEBOUNCE_MS = 300
export const MIN_QUERY_LENGTH = 3
const MAX_RECENT = 10

function recentKey(userId: string): string {
    return userId ? `numeo_recent_searches_${userId}` : 'numeo_recent_searches'
}

function readRecentFromStorage(key: string): string[] {
    try {
        return JSON.parse(localStorage.getItem(key) || '[]')
    } catch {
        return []
    }
}

function formatPeriod(start: string, end: string): string {
    const fmt = (iso: string) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '')
    const s = fmt(start)
    const e = fmt(end)
    if (s && e) return `${s} – ${e}`
    return s || e
}

function mapResultsToItems(data: GlobalSearchResponse, boxTruckProfilesMode: boolean): SearchResultItem[] {
    const items: SearchResultItem[] = []

    for (const load of data.loads) {
        items.push({
            id: load.id,
            category: SearchCategory.Loads,
            title: load.sourceRateConfId || `Load #${load.id}`,
            subtitle: [load.companyName, `${load.origin} → ${load.destination}`, load.status].filter(Boolean).join(' · '),
            // The loads route is keyed on the rate-conf id; falling back to the raw Mongo _id would 404 / open
            // the wrong load. Backend results always carry sourceRateConfId, so default to the list page if absent.
            path: load.sourceRateConfId ? `/updater-agent/loads/${load.sourceRateConfId}` : '/updater-agent/loads',
            // Hand the detail page the record id we already have. Without it the page re-resolves the load from
            // the URL segment through an unanchored substring search capped at one row, sorted newest-first — so
            // a longer, newer rate-conf id containing this one wins and a different load opens.
            state: { loadId: load.id },
        })
    }

    for (const truck of data.trucks) {
        items.push({
            id: truck.id,
            category: SearchCategory.Trucks,
            title: truck.mainInfo || truck.truckId || `Truck #${truck.id}`,
            subtitle: [truck.truckId !== truck.mainInfo ? truck.truckId : '', truck.status].filter(Boolean).join(' · '),
            path: '/profiles/trucks',
        })
    }

    for (const driver of data.drivers) {
        items.push({
            id: driver.id,
            category: SearchCategory.Drivers,
            title: driver.name || driver.email || 'Unknown Driver',
            subtitle: [driver.phone, driver.email, driver.truckId ? `Truck: ${driver.truckId}` : ''].filter(Boolean).join(' · '),
            path: '/profiles/drivers',
        })
    }

    for (const invoice of data.invoices || []) {
        const formattedAmount = invoice.totalDue ? `$${invoice.totalDue.toLocaleString('en-US', { minimumFractionDigits: 2 })}` : ''
        // Prefer the load number (see `path` below); fall back to the invoice number for a batch
        // invoice whose loads didn't come back, so the table is still narrowed to something.
        const searchKey = invoice.loadNumbers?.[0] || invoice.invoiceNumber
        items.push({
            id: invoice.id,
            category: SearchCategory.Invoices,
            title: `INV #${invoice.invoiceNumber}`,
            subtitle: [invoice.brokerName, formattedAmount, invoice.loadNumbers?.length ? `Loads: ${invoice.loadNumbers.join(', ')}` : '']
                .filter(Boolean)
                .join(' · '),
            // Two things have to be right for the row to actually be on screen:
            //  - tab: the server tells us whether this invoice is aged. Invoiced is a superset (the
            //    rows pipeline only narrows on the Aged tab), but an overdue invoice is buried there
            //    among every other one — Aged is the view the dispatcher is actually looking for.
            //  - search: the table is server-paginated, so an invoice on page 7 never mounts and the
            //    highlight finds nothing. Seeding the page's search box narrows the query to page 1.
            //
            // Search by LOAD number, not invoice number. A STANDARD invoice whose load is still
            // non-batched is suppressed from the rows pipeline (buildStandaloneSuppressionStages) and
            // renders as a load pseudo-row instead — and that pseudo-row's invoiceNumber is the LOAD
            // number, not the real one. Seeding the real invoice number therefore matched nothing and
            // emptied the table. The load number matches both shapes: the real invoice via
            // loads.loadNumber, and the pseudo-row via its load-derived invoiceNumber.
            path: `/accounting/invoices?tab=${invoice.aged ? 'aged' : 'invoiced'}${searchKey ? `&search=${encodeURIComponent(searchKey)}` : ''}`,
            // Rendered as a badge next to the row — the status is the first thing a dispatcher
            // looks for when they search an invoice by load id.
            status: invoice.status,
        })
    }

    for (const broker of data.brokers) {
        items.push({
            id: broker.id,
            category: SearchCategory.Brokers,
            title: broker.companyName || 'Unknown Broker',
            subtitle: [broker.mcNumber ? `MC: ${broker.mcNumber}` : '', broker.domain].filter(Boolean).join(' · '),
            path: '/profiles/brokers',
        })
    }

    for (const contractor of data.contractors ?? []) {
        items.push({
            id: contractor.id,
            category: SearchCategory.Contractors,
            title: contractor.name || (boxTruckProfilesMode ? 'Unknown Owner' : 'Unknown Contractor'),
            subtitle: [contractor.phone, contractor.email].filter(Boolean).join(' · '),
            path: '/profiles/contractors',
            // Only the Box Truck owner directory is server-paged. Keep TMS global-search's
            // original highlighted-row navigation rather than opening its edit form directly.
            ...(boxTruckProfilesMode ? { state: { openContractorId: contractor.id } } : {}),
        })
    }

    for (const payroll of data.payroll ?? []) {
        const period = formatPeriod(payroll.periodStart, payroll.periodEnd)
        const formattedNetPay =
            typeof payroll.netPay === 'number' ? `$${payroll.netPay.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : ''
        const roleLabel = payroll.type === 'contractor' ? 'Contractor' : 'Driver'
        items.push({
            id: payroll.id,
            category: SearchCategory.Payroll,
            title: payroll.name || `${roleLabel} payroll`,
            subtitle: [period, formattedNetPay, payroll.status].filter(Boolean).join(' · '),
            path: payroll.type === 'contractor' ? `/accounting/contractor-pay/${payroll.id}` : `/accounting/driver-pay/${payroll.id}`,
        })
    }

    for (const trip of data.trips) {
        items.push({
            id: trip.id,
            category: SearchCategory.Trips,
            title: `Trip · ${trip.truckId}`,
            subtitle: [trip.status, `${trip.loadsCount} loads`, trip.totalDistance ? `${Math.round(trip.totalDistance)} mi` : ''].filter(Boolean).join(' · '),
            path: '/updater-agent/loads',
        })
    }

    return items
}

export function useGlobalSearch() {
    const userId = useAppSelector((state) => state.auth.user?.id ?? '')
    const boxTruckProfilesMode = useBoxTruckProfilesMode()
    const storageKey = recentKey(userId)

    const [query, setQuery] = useState('')
    // Scope is intentionally in-memory only: a fresh page load defaults back to "All" so a narrow
    // scope left over from a past session can't silently hide results the user expects to see.
    const [scope, setScopeState] = useState<SearchScope>('all')
    const [results, setResults] = useState<SearchResultItem[]>([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [activeIndex, setActiveIndex] = useState(0)
    const abortRef = useRef<AbortController | null>(null)
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    const setScope = useCallback((next: SearchScope) => setScopeState(next), [])

    const search = useCallback(
        async (q: string, scopeArg: SearchScope) => {
            if (q.length < MIN_QUERY_LENGTH) {
                setResults([])
                setLoading(false)
                return
            }

            abortRef.current?.abort()
            const controller = new AbortController()
            abortRef.current = controller

            setLoading(true)
            setError(null)
            try {
                const res = await axios.get<{ success: boolean; data: GlobalSearchResponse }>('/global-search/search', {
                    // Only send `category` when scoped — omitting it keeps the default all-8 search.
                    params: { q, limit: 5, ...(scopeArg !== 'all' && { category: scopeArg }), ...(boxTruckProfilesMode && { boxTruckProfiles: true }) },
                    signal: controller.signal,
                })

                if (!controller.signal.aborted && res.data.success) {
                    const items = mapResultsToItems(res.data.data, boxTruckProfilesMode)
                    setResults(items)
                    setActiveIndex(0)
                    if (res.data.data.errors?.length) {
                        setError(`Some categories failed: ${res.data.data.errors.join(', ')}`)
                    }
                }
            } catch (err) {
                // Ignore axios cancellations / aborts — they're expected on every keystroke.
                if (axios.isCancel(err) || (err instanceof Error && err.name === 'CanceledError')) {
                    return
                }
                if (!controller.signal.aborted) {
                    setResults([])
                    setError('Search failed. Please try again.')
                }
            } finally {
                if (!controller.signal.aborted) {
                    setLoading(false)
                }
            }
        },
        [boxTruckProfilesMode]
    )

    useEffect(() => {
        if (timerRef.current) clearTimeout(timerRef.current)

        const trimmed = query.trim()
        // Below the minimum length there's nothing to search — abort any in-flight request and
        // clear immediately so no stale results linger through the debounce window.
        if (trimmed.length < MIN_QUERY_LENGTH) {
            abortRef.current?.abort()
            setResults([])
            setLoading(false)
            return
        }

        // Flip to loading synchronously on keystroke (before the debounce fires) so the dropdown
        // never falls into the empty/"no results" state during the 300ms gap — that gap was the
        // source of the flicker. Previous results stay visible underneath until the new set lands.
        setLoading(true)

        // scope is a dependency: switching category re-runs the current query against the new scope.
        timerRef.current = setTimeout(() => search(trimmed, scope), DEBOUNCE_MS)

        return () => {
            if (timerRef.current) clearTimeout(timerRef.current)
        }
    }, [query, scope, search])

    useEffect(() => {
        return () => {
            abortRef.current?.abort()
        }
    }, [])

    const [recents, setRecents] = useState<string[]>([])

    // Reload recents whenever the storage key changes (user login/switch)
    useEffect(() => {
        setRecents(readRecentFromStorage(storageKey))
    }, [storageKey])

    const saveRecentSearch = useCallback(
        (q: string) => {
            const trimmed = q.trim()
            if (!trimmed) return
            setRecents((prev) => {
                const updated = [trimmed, ...prev.filter((s) => s !== trimmed)]
                if (updated.length > MAX_RECENT) updated.length = MAX_RECENT
                localStorage.setItem(storageKey, JSON.stringify(updated))
                return updated
            })
        },
        [storageKey]
    )

    const removeRecentSearch = useCallback(
        (q: string) => {
            setRecents((prev) => {
                const updated = prev.filter((s) => s !== q)
                localStorage.setItem(storageKey, JSON.stringify(updated))
                return updated
            })
        },
        [storageKey]
    )

    const clearRecentSearches = useCallback(() => {
        localStorage.removeItem(storageKey)
        setRecents([])
    }, [storageKey])

    return {
        query,
        setQuery,
        scope,
        setScope,
        results,
        loading,
        error,
        activeIndex,
        setActiveIndex,
        recents,
        saveRecentSearch,
        removeRecentSearch,
        clearRecentSearches,
    }
}
